import type { DollarsString } from './dollars';
import type { PriceLevelStructure } from './priceLevelStructure';
import type { SearchResultMarket } from './search';

/*
  Using union types for defining order statuses.
  Reasoning explained here: https://blog.bam.tech/developer-news/should-you-use-enums-or-union-types-in-typescript
 */
export const marketStatuses = [
    'invalid',
    'initialized',
    'inactive',
    'active',
    'closed',
    'determined',
    'disputed',
    'amended',
    'settled',
    'finalized',
] as const;

export type MarketStatus = (typeof marketStatuses)[number];

export const marketStatusFilters = [
    'open',
    'closed',
    'settled',
    'unopened',
    'open-single',
    'closed-single',
    'settled-single',
    'unopened-single',
];

export const completeMarketStatusFilters = ['open', 'closed', 'settled', 'unopened'];

export type MarketStatusFilter = (typeof marketStatusFilters)[number];

export type MarketResult = '' | 'yes' | 'no' | 'scalar' | 'void' | 'invalid';

export interface SettlementSource {
    url: string;
    name: string;
}

// It can assume multiple property names and values depending on the milestone type
// Stat-ladder (scalar payout) markets carry `scalar_payout_type`, `scalar_floor`,
// `scalar_cap`, `maximum_payout_dollars`, `statistic` and `entity_type` here — see
// `queries/ladderMarket.ts`, which owns their parsing.
export interface CustomStrike {
    [key: string]: string;
}

/** Returns the first customStrike value whose key contains "player" or "team". */
export function getCustomStrikeTarget(customStrike: CustomStrike | undefined): string | undefined {
    if (!customStrike) return undefined;
    return Object.entries(customStrike).find(([key]) => key.includes('player') || key.includes('team'))?.[1];
}

/** Structured-target id of the player a strike is about (first `*player*` key holding a target uuid). */
export function getCustomStrikePlayerId(customStrike: CustomStrike | undefined): string | undefined {
    if (!customStrike) return undefined;
    return Object.entries(customStrike).find(
        ([key, value]) => key.toLowerCase().includes('player') && isStructuredTargetUuid(value),
    )?.[1];
}

/** Returns the first customStrike value whose key contains "team". */
export function getCustomStrikeTeam(customStrike: CustomStrike | undefined): string | undefined {
    if (!customStrike) return undefined;
    return Object.entries(customStrike).find(([key]) => key.includes('team'))?.[1];
}

/** Album-scoped strikes are per-release; they should not share milestone-level artist stream live data. */
export function customStrikeHasAlbum(customStrike: CustomStrike | undefined): boolean {
    return !!getCustomStrikeAlbumId(customStrike);
}

/** Returns the album structured target UUID from a custom strike, if present. */
export function getCustomStrikeAlbumId(customStrike: CustomStrike | undefined): string | undefined {
    if (!customStrike) return undefined;
    const album = customStrike.album;
    return typeof album === 'string' && album.length > 0 ? album : undefined;
}

const STRUCTURED_TARGET_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when a custom strike value is a structured-target UUID (vs a numeric strike / plain name). */
export function isStructuredTargetUuid(value: string): boolean {
    return STRUCTURED_TARGET_UUID.test(value);
}

/** Deduped structured-target UUIDs from a market's custom_strike values, in entry order. */
export function getCustomStrikeStructuredTargetIds(customStrike: CustomStrike | undefined): string[] {
    if (!customStrike) return [];
    const ids = Object.values(customStrike).filter(
        (value): value is string => typeof value === 'string' && isStructuredTargetUuid(value),
    );
    return Array.from(new Set(ids));
}

/**
 * Deduped structured-target UUIDs across several markets' custom_strike values.
 * Mirrors `MilestonesStore.loadCustomStrikeStructuredTargets`'s id-collection
 * step — pass the result to `useStructuredTargetsQuery`, which already
 * dedupes/caches by id, so no separate "already cached" filter is needed here.
 */
export function getStructuredTargetIdsFromCustomStrikes(customStrikes: Array<CustomStrike | undefined>): string[] {
    const ids = customStrikes.flatMap((customStrike) => getCustomStrikeStructuredTargetIds(customStrike));
    return Array.from(new Set(ids));
}

/**
 * Returns the structured-target UUID that represents the outcome of a score-based market
 * (NFL, MLB, NBA, etc.) — i.e. the home team ID when home_score > away_score, the away
 * team ID when away_score > home_score, or tie_id when scores are equal.
 * Returns undefined for non-score-based strikes (soccer, esports single-team keys, etc.).
 */
export function getScoreResultTargetId(customStrike: CustomStrike | undefined): string | undefined {
    const homeTeamId = customStrike?.home_team_id;
    const awayTeamId = customStrike?.away_team_id;
    const homeScore = customStrike?.home_score !== undefined ? parseInt(customStrike.home_score, 10) : undefined;
    const awayScore = customStrike?.away_score !== undefined ? parseInt(customStrike.away_score, 10) : undefined;
    if (!homeTeamId || !awayTeamId || homeScore === undefined || awayScore === undefined) return undefined;
    if (isNaN(homeScore) || isNaN(awayScore)) return undefined;
    if (homeScore > awayScore) return homeTeamId;
    if (awayScore > homeScore) return awayTeamId;
    return customStrike?.tie_id;
}

export type { PriceLevelStructure };

/**
 * Returns true when the given cents price represents an actionable offer.
 * Prices of 0 (no bid) or 100 (no ask) indicate an empty side of the book — the
 * canonical "no liquidity" sentinels for both YES bid/ask and the NO side
 * derived as `100 - yes_*`.
 */
export const hasLiquidity = (price: number): boolean => price > 0 && price < 100;

export type StrikeType =
    | 'greater'
    | 'greater_or_equal'
    | 'less'
    | 'less_or_equal'
    | 'between'
    | 'structured'
    | 'custom'
    | 'unknown'
    | '';

export interface MveSelectedLeg {
    event_ticker: string;
    market_ticker: string;
    side: 'yes' | 'no';
    event_title: string;
    leg_title: string;
    /** `leg_title` with the event named too — "Over 2.5 goals scored (Arsenal
     * vs Coventry: Total Goals)". Disambiguates legs whose `leg_title` alone
     * repeats within a matchup. */
    leg_title_with_context?: string;
    settlement_value_centicents: number;
    result: MarketResult;
}

/** One matchup inside `mve_selected_legs_by_category` — every leg the combo
 * takes on a single game, under the game's own label ("Arsenal vs Coventry"). */
export interface MveSelectedLegGroup {
    label: string;
    milestone_id?: string;
    color_palettes?: string[];
    legs: MveSelectedLeg[];
}

/** One league inside `mve_selected_legs_by_category` ("EPL", "Pro Baseball",
 * "ATP Cincinnati"), holding its matchups. The backend's own grouping of a
 * combo market's legs — the combo builder renders its panel from it. */
export interface MveSelectedLegCategory {
    label: string;
    category?: string;
    milestone_type?: string;
    groups: MveSelectedLegGroup[];
}

export interface MarketVo {
    id: string;
    ticker_name: string;
    title: string;
    sub_title: string;
    yes_sub_title: string;
    description_case_yes?: string;
    description_case_no?: string;

    list_date: string;
    open_date: string;
    close_date: string;
    fee_waiver_expiration_date?: string;
    expiration_date: string;
    settlement_timer_seconds: number;
    settlement_value_centicents: number;

    yes_bid: number;
    yes_ask: number;
    yes_bid_dollars: DollarsString;
    yes_ask_dollars: DollarsString;

    last_price: number; // from Yes perspective
    last_price_dollars: DollarsString;
    prev_period_price?: number; // last last_price from previous period based on series frequency
    prev_period_price_dollars?: DollarsString;

    previous_hour_price?: number; // last last_price from previous hour
    previous_day_price?: number; // last last_price from previous day
    previous_week_price?: number; // last last_price from previous week

    volume: number;
    dollar_volume: number; // is (volume / 2)
    recent_volume: number; // difference between volume from 24hr ago and volume now
    dollar_recent_volume: number; // is (recent_volume / 2)
    open_interest: number;

    status: MarketStatus;
    result: MarketResult;
    expiration_value: string;
    expected_expiration_date: string;
    rulebook_variables: { [variable: string]: string };
    strike_type?: StrikeType;
    floor_strike?: number;
    cap_strike?: number;

    risk_limit_cents: number;
    event_ticker?: string; // Owning event's ticker
    ranged_group_ticker?: string; // Event ticker sent for compatibility

    price_level_structure?: PriceLevelStructure;

    custom_strike?: CustomStrike;
    /** The structured target the market is about (leader/award option markets). */
    structured_target_id?: string;
    /** How this market reads as one leg of a combo — "Frances Tiafoe to win
     * set 1" where the market's own subtitle is the bare "Frances Tiafoe".
     * Self-describing, because a combo lists it away from the event that gives
     * it context. */
    component_leg_title?: string;
    mve_selected_legs?: MveSelectedLeg[];
    mve_selected_legs_by_category?: MveSelectedLegCategory[];
    mve_collection_ticker?: string;

    // Stubbing properties
    create_date?: string;
    dollar_open_interest?: number;
    mini_title?: string;
    no_sub_title?: string;
    name?: string;
    can_close_early?: boolean;
    previous_preriod_price?: number;
    fractional_trading_enabled?: boolean;
}

export const mapSearchResultMarketToMarketVo = (market: SearchResultMarket): MarketVo => {
    return {
        id: market.ticker,
        ticker_name: market.ticker,
        title: market.yes_subtitle,
        sub_title: '',
        yes_sub_title: market.yes_subtitle,
        list_date: '',
        open_date: market.open_ts,
        close_date: market.close_ts,
        expiration_date: market.close_ts,
        settlement_timer_seconds: 0,
        settlement_value_centicents: 0,
        yes_bid: market.yes_bid,
        yes_ask: market.yes_ask,
        yes_bid_dollars: `${(market.yes_bid / 100).toFixed(4)}`,
        yes_ask_dollars: `${(market.yes_ask / 100).toFixed(4)}`,
        last_price: market.last_price,
        last_price_dollars: `${(market.last_price / 100).toFixed(4)}`,
        volume: 0,
        dollar_volume: 0,
        recent_volume: 0,
        dollar_recent_volume: 0,
        open_interest: 0,
        status: 'active',
        result: market.result || '',
        expiration_value: '',
        expected_expiration_date: '',
        rulebook_variables: market.rulebook_variables,
        risk_limit_cents: 0,
    };
};
